import { loadImageDom } from './fs/image.utils';

export const mirrorImg = async (
  dataUrl: string,
  dir: 'horizontal' | 'vertical',
  mimetype?: string
) => {
  mimetype =
    mimetype ??
    dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';'));
  const sourceImg = new Image();
  const [width, height] = await new Promise<[number, number]>((res) => {
    sourceImg.addEventListener('load', () =>
      res([sourceImg.width, sourceImg.height])
    );
    sourceImg.src = dataUrl;
  });
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [width, height];
  const horizontal = dir === 'horizontal';
  ctx.translate(horizontal ? width : 0, horizontal ? 0 : height);
  ctx.scale(horizontal ? -1 : 1, horizontal ? 1 : -1);
  ctx.drawImage(sourceImg, 0, 0);
  return canvas.toDataURL(mimetype);
};

export const resizeImg = async (
  dataUrl: string | undefined,
  options: { width: number; height: number; mimetype?: string }
) => {
  if (!dataUrl) return;
  const mimetype =
    options.mimetype ??
    dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';'));
  const sourceImg = new Image();
  const { width, height } = options;
  await new Promise<void>((res) => {
    sourceImg.addEventListener('load', () => res());
    sourceImg.src = dataUrl;
  });
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [width, height];
  ctx.drawImage(sourceImg, 0, 0, width, height);
  return canvas.toDataURL(mimetype);
};

/**
 * 像素级圆角裁剪（带抗锯齿）：圆角外的像素设为透明或填充指定颜色，
 * 边缘像素根据到圆弧的距离做 alpha 渐变，避免锯齿
 * @param fillColor 可选，圆角外填充颜色，不传则设为透明
 */
export const clipRoundImg = async (
  dataUrl: string | undefined,
  clipRound: number,
  fillColor?: string
) => {
  if (!dataUrl || !clipRound) return dataUrl;
  const sourceImg = await loadImageDom(dataUrl);
  if (!sourceImg) return dataUrl;
  const w = sourceImg.width;
  const h = sourceImg.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(sourceImg, 0, 0);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  const cr = Math.min(clipRound, w / 2, h / 2);
  // 解析fillColor
  let fr = 0, fg = 0, fb = 0, fa = 0;
  if (fillColor) {
    const tmp = document.createElement('canvas');
    tmp.width = 1; tmp.height = 1;
    const tc = tmp.getContext('2d')!;
    tc.fillStyle = fillColor;
    tc.fillRect(0, 0, 1, 1);
    const p = tc.getImageData(0, 0, 1, 1).data;
    fr = p[0]; fg = p[1]; fb = p[2]; fa = p[3];
  }
  // 计算像素到圆角边界的距离，返回 coverage（0=完全在外，1=完全在内）
  const getCornerCoverage = (px: number, py: number, cx: number, cy: number): number => {
    const dx = px - cx, dy = py - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // 3像素宽过渡带 + smoothstep曲线
    if (dist <= cr - 1.5) return 1;
    if (dist >= cr + 1.5) return 0;
    const t = (cr + 1.5 - dist) / 3; // 0~1
    return t * t * (3 - 2 * t); // smoothstep
  };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let coverage = 1; // 1=完全在内
      // 扩大2像素的判断范围，确保3px过渡带内的像素都被处理
      const crCheck = cr + 2;
      if (x < crCheck && y < crCheck) {
        coverage = getCornerCoverage(x, y, cr, cr);
      } else if (x > w - crCheck && y < crCheck) {
        coverage = getCornerCoverage(x, y, w - cr, cr);
      } else if (x < crCheck && y > h - crCheck) {
        coverage = getCornerCoverage(x, y, cr, h - cr);
      } else if (x > w - crCheck && y > h - crCheck) {
        coverage = getCornerCoverage(x, y, w - cr, h - cr);
      }
      if (coverage < 1) {
        const i = (y * w + x) * 4;
        if (coverage <= 0) {
          data[i] = fr; data[i + 1] = fg; data[i + 2] = fb; data[i + 3] = fa;
        } else {
          const origA = data[i + 3] / 255;
          if (fillColor) {
            // fillColor模式：边缘像素保持原始内容色不变，不与fillColor混合
            // 只有完全在外的像素才填充fillColor（上面coverage<=0的分支）
            // 这样圆角→直边过渡处不会出现颜色跳变
          } else {
            // 透明模式：保持RGB不变，只降低alpha
            if (origA > 0) {
              data[i + 3] = Math.round(origA * coverage * 255);
            }
          }
        }
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);
  const mimetype = dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';')) || 'image/png';
  return canvas.toDataURL(mimetype, 1.0);
};

export const addTansparentBorder = async (
  dataUrl: string,
  borderPix: {
    top: number;
    right: number;
    left: number;
    bottom: number;
  }
) => {
  if (!dataUrl) return;
  const imgDom = await loadImageDom(dataUrl);
  if (!imgDom) return;
  const { width, height } = imgDom;
  const { top, bottom, left, right } = borderPix;
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [width + left + right, height + top + bottom];
  ctx.drawImage(imgDom, top, left);
  return canvas.toDataURL();
};

export const clipImg = async (
  dataUrl: string,
  options: {
    x: number;
    y: number;
    width: number;
    height: number;
    mimetype?: string;
  }
) => {
  if (!dataUrl) return;
  const mimetype =
    options.mimetype ??
    dataUrl.substring(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';'));
  const sourceImg = new Image();
  const { x, y, width, height } = options;
  await new Promise<void>((res) => {
    sourceImg.addEventListener('load', () => res());
    sourceImg.src = dataUrl;
  });
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [width, height];
  ctx.drawImage(sourceImg, x, y, width, height, 0, 0, width, height);
  return canvas.toDataURL(mimetype);
};

const getRotatedDimensions = (w: number, h: number, rad: number) => {
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  return {
    w: h * sin + w * cos,
    h: h * cos + w * sin
  };
};

export const clipImgByPath = async (dataUrl: string, path: string) => {
  const img = (await loadImageDom(dataUrl)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [img.height, img.width];
  ctx.clip(new Path2D(path));
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL();
};

export const rotateImg = async (dataUrl: string, deg: number) => {
  const img = (await loadImageDom(dataUrl)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const rad = (deg * Math.PI) / 180;
  // 计算旋转后画布尺寸
  const rotated = getRotatedDimensions(img.width, img.height, rad);
  [canvas.width, canvas.height] = [rotated.w, rotated.h];
  // 图片中心坐标
  const cx = rotated.w / 2,
    cy = rotated.h / 2;
  // 图片中心旋转后偏移坐标
  const offset = [(rotated.w - img.width) / 2, (rotated.h - img.height) / 2];
  // 移动原点到画布中心, 并旋转画布
  ctx.translate(cx, cy);
  ctx.rotate(rad);
  // 原点移回画布左上角, 并绘制图片
  ctx.drawImage(img, cx - img.width / 2, cy - img.height / 2);
  return { dataUrl: canvas.toDataURL(), offset };
};

export const rotateImg90deg = async (dataUrl: string, isClockwise = true) => {
  const img = (await loadImageDom(dataUrl)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [img.height, img.width];
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(isClockwise ? Math.PI / 2 : -Math.PI / 2);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);
  const rotatedImage = canvas.toDataURL();
  return rotatedImage;
};

export const applyOpacity = async (dataUrl: string, opacity: number) => {
  const img = (await loadImageDom(dataUrl)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [img.height, img.width];
  ctx.globalAlpha = opacity;
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL();
};

export const getGrayScaleValue = async (dataUrl: string) => {
  const img = (await loadImageDom(dataUrl)) as HTMLImageElement;
  if (!img.width || !img.height) return 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', {
    willReadFrequently: true
  }) as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [img.width, img.height];
  ctx.drawImage(img, 0, 0);
  const imgData = ctx.getImageData(0, 0, img.width, img.height).data;
  let sum = 0;
  for (let i = 0; i < img.width * img.height; i++) {
    const r = imgData[i * 4];
    const g = imgData[i * 4 + 1];
    const b = imgData[i * 4 + 2];
    sum += r + g + b;
  }
  return sum / (img.width * img.height) / 3;
};

export const hasTransparentPixel = async (
  dataUrl: string
): Promise<boolean> => {
  const img = (await loadImageDom(dataUrl)) as HTMLImageElement;
  if (!img.width || !img.height) return false;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', {
    willReadFrequently: true
  }) as CanvasRenderingContext2D;
  [canvas.width, canvas.height] = [img.width, img.height];
  ctx.drawImage(img, 0, 0);
  const imgData = ctx.getImageData(0, 0, img.width, img.height).data;
  for (let i = 0; i < imgData.length; i += 4)
    if (imgData[i + 3] < 255) return true;
  return false;
};

export const makeTransparent = (
  imagePath: string,
  centerX: number,
  centerY: number,
  circleRadius: number
) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imagePath;
    image.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d', {
        willReadFrequently: true
      });
      if (!ctx) return;
      ctx.drawImage(image, 0, 0);
      const imgData = ctx.getImageData(0, 0, image.width, image.height);
      const data = imgData.data;
      // 指定路径的图片距离中心为circleCenter，半径为circleRadius的圆形区域的部分变为透明
      for (let x = 0; x < image.width; x++) {
        for (let y = 0; y < image.height; y++) {
          const dx = x - centerX;
          const dy = y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance > circleRadius) {
            const index = (y * image.width + x) * 4;
            data[index] = 0;
            data[index + 1] = 0;
            data[index + 2] = 0;
            data[index + 3] = 0;
          }
        }
      }
      ctx.putImageData(imgData, 0, 0);
      const dataURL = canvas.toDataURL();
      resolve(dataURL);
    };
    image.onerror = (error) => reject(error);
  });
};
